import { useState, useEffect, useCallback } from 'react'
import fetchAPI from '../../../../lib/utils/fetch-api'
import { EmailSystemStatus, ConnectionInfo } from '../interfaces/interfaces'

export const useFetchData = () => {
  const [status, setStatus] = useState<EmailSystemStatus | null>(null)
  const [connections, setConnections] = useState<ConnectionInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetchAPI('/api/email-monitor/status')
      if (response.success) {
        setStatus(response.data)
      }
    } catch (error) {
      console.error('Error fetching status:', error)
    }
  }, [])

  const fetchConnections = useCallback(async () => {
    try {
      const response = await fetchAPI('/api/email-monitor/connections')
      if (response.success) {
        setConnections(response.data)
      }
    } catch (error) {
      console.error('Error fetching connections:', error)
    }
  }, [])

  const refreshAll = useCallback(async () => {
    setLoading(true)
    await Promise.all([fetchStatus(), fetchConnections()])
    setLoading(false)
  }, [fetchStatus, fetchConnections])

  const cleanupStuck = useCallback(async () => {
    try {
      const response = await fetchAPI('/api/email-monitor/cleanup', { method: 'POST' })
      if (response.success) {
        return response.data
      }
    } catch (error) {
      console.error('Error cleaning up:', error)
      throw error
    }
  }, [])

  const clearConnections = useCallback(async () => {
    try {
      const response = await fetchAPI('/api/email-monitor/clear-connections', { method: 'POST' })
      if (response.success) {
        return response.data
      }
    } catch (error) {
      console.error('Error clearing connections:', error)
      throw error
    }
  }, [])

  const retryFailed = useCallback(async () => {
    try {
      const response = await fetchAPI('/api/email-monitor/queue/retry-failed', {
        method: 'POST',
        body: JSON.stringify({ maxAge: 24 })
      })
      if (response.success) {
        return response.data
      }
    } catch (error) {
      console.error('Error retrying failed:', error)
      throw error
    }
  }, [])

  useEffect(() => {
    refreshAll()
  }, [])

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchStatus()
        fetchConnections()
      }, 5000)
      return () => clearInterval(interval)
    }
  }, [autoRefresh, fetchStatus, fetchConnections])

  return {
    status,
    connections,
    loading,
    autoRefresh,
    setAutoRefresh,
    refreshAll,
    cleanupStuck,
    clearConnections,
    retryFailed
  }
}

